// Stylised materials: every opaque surface uses toon shading with one shared, warm light ramp
// (the illustrated-board look of the key art). PBR is kept only for glass.
import * as THREE from 'three';

let ramp: THREE.DataTexture | null = null;

/** Four light steps: deep shade, shade, lit, highlight. */
export function toonRamp(): THREE.DataTexture {
  if (!ramp) {
    const steps = [92, 150, 214, 255];
    const data = new Uint8Array(steps.length * 4);
    steps.forEach((v, i) => data.set([v, v, v, 255], i * 4));
    ramp = new THREE.DataTexture(data, steps.length, 1, THREE.RGBAFormat);
    ramp.minFilter = ramp.magFilter = THREE.NearestFilter;
    ramp.generateMipmaps = false;
    ramp.needsUpdate = true;
  }
  return ramp;
}

export interface ToonParams {
  color?: THREE.ColorRepresentation;
  map?: THREE.Texture | null;
  emissive?: THREE.ColorRepresentation;
  emissiveIntensity?: number;
  transparent?: boolean;
  opacity?: number;
  side?: THREE.Side;
  depthWrite?: boolean;
  alphaTest?: number;
  /** Metals read brighter under a ramp than under PBR; nudge them so brass still looks like brass. */
  metal?: boolean;
}

export function toon(p: ToonParams = {}): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({
    color: p.color ?? '#ffffff',
    map: p.map ?? null,
    gradientMap: toonRamp(),
    emissive: p.emissive ?? '#000000',
    emissiveIntensity: p.emissiveIntensity ?? 1,
    transparent: p.transparent ?? false,
    opacity: p.opacity ?? 1,
    side: p.side ?? THREE.FrontSide,
    depthWrite: p.depthWrite ?? true,
    alphaTest: p.alphaTest ?? 0,
  });
  if (p.metal) m.color.offsetHSL(0, 0.05, 0.06);
  return m;
}

/** Accepts the old MeshStandard-style parameter bag (roughness/metalness are folded in). */
export function toonFrom(p: THREE.MeshStandardMaterialParameters & { metal?: boolean }): THREE.MeshToonMaterial {
  const params: ToonParams = {};
  if (p.color !== undefined) params.color = p.color;
  if (p.map !== undefined) params.map = p.map;
  if (p.emissive !== undefined) params.emissive = p.emissive;
  if (p.emissiveIntensity !== undefined) params.emissiveIntensity = p.emissiveIntensity;
  if (p.transparent !== undefined) params.transparent = p.transparent;
  if (p.opacity !== undefined) params.opacity = p.opacity;
  if (p.side !== undefined) params.side = p.side;
  if (p.depthWrite !== undefined) params.depthWrite = p.depthWrite;
  if (p.alphaTest !== undefined) params.alphaTest = p.alphaTest;
  params.metal = p.metal ?? (p.metalness ?? 0) > 0.5;
  return toon(params);
}

/** Convert a loaded (glTF) standard material into the toon look, keeping name, colour and maps. */
export function toToon(src: THREE.MeshStandardMaterial): THREE.Material {
  if (src.name === 'glass') return src;
  const m = toon({
    color: src.color,
    map: src.map,
    emissive: src.emissive,
    emissiveIntensity: src.emissiveIntensity,
    transparent: src.transparent,
    opacity: src.opacity,
    side: src.side,
    metal: src.metalness > 0.5,
  });
  m.name = src.name;
  return m;
}
